"use client";

import { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { gsap } from "gsap";
import { Flip } from "gsap/Flip";
import { Layers } from "lucide-react";
import { SITE_TOOL_ENTRIES } from "@/lib/site-config";
import { useCanHover, useIsTabletOrBelow } from "@/lib/use-media-query";
import { cn } from "@/lib/utils";

if (typeof window !== "undefined") {
  gsap.registerPlugin(Flip);
}

export default function FanOutDeck() {
  const canHover = useCanHover();
  const isTabletOrBelow = useIsTabletOrBelow();
  const [isGrid, setIsGrid] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // On touch/tablet: default to grid so all tools are visible without hover
  useEffect(() => {
    if (initializedRef.current) return;
    if (!canHover || isTabletOrBelow) {
      setIsGrid(true);
      initializedRef.current = true;
    }
  }, [canHover, isTabletOrBelow]);

  // Auto-shuffle interval when stacked (not in grid mode)
  useEffect(() => {
    if (isGrid) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % SITE_TOOL_ENTRIES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isGrid]);

  const toggleLayout = (targetState: boolean) => {
    if (isGrid === targetState) return;

    const state = Flip.getState(".brand-card");

    flushSync(() => {
      setIsGrid(targetState);
    });

    Flip.from(state, {
      duration: 0.8,
      ease: "power3.out",
      absolute: true,
      stagger: targetState ? 0.03 : -0.02,
      spin: targetState ? 1 : -1,
      onEnter: (elements) =>
        gsap.fromTo(
          elements,
          { opacity: 0, scale: 0.8 },
          { opacity: 1, scale: 1, duration: 0.8 },
        ),
      onLeave: (elements) =>
        gsap.to(elements, { opacity: 0, scale: 0.8, duration: 0.8 }),
    });
  };

  const handleTap = () => {
    if (canHover && !isTabletOrBelow) return;
    toggleLayout(!isGrid);
  };

  const handleMouseEnter = () => {
    if (canHover && !isTabletOrBelow) toggleLayout(true);
  };

  const handleMouseLeave = () => {
    if (canHover && !isTabletOrBelow) toggleLayout(false);
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleTap}
      role={!canHover || isTabletOrBelow ? "button" : undefined}
      tabIndex={!canHover || isTabletOrBelow ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && (!canHover || isTabletOrBelow)) {
          e.preventDefault();
          handleTap();
        }
      }}
      className={cn(
        "relative flex w-full min-h-[320px] items-center justify-center perspective-1000 transition-all duration-700 ease-in-out sm:min-h-[380px] md:min-h-[440px]",
        !canHover || isTabletOrBelow ? "cursor-pointer" : "cursor-default",
      )}
    >
      <div
        className={
          isGrid
            ? "mx-auto grid w-full max-w-6xl grid-cols-2 gap-3 px-2 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 xl:gap-5"
            : "relative h-[150px] w-[240px] sm:h-[180px] sm:w-[280px] md:w-[320px]"
        }
        style={!isGrid ? { transformStyle: "preserve-3d" } : {}}
      >
        {SITE_TOOL_ENTRIES.map((item, idx) => {
          const relativeIdx =
            (idx - activeIndex + SITE_TOOL_ENTRIES.length) %
            SITE_TOOL_ENTRIES.length;
          const isTop = relativeIdx === 0;

          const gridClasses =
            "relative flex h-28 flex-col items-center justify-center rounded-2xl border border-neutral-200/50 bg-white/40 p-4 shadow-sm transition-colors duration-500 glass-panel dark:border-neutral-800/60 dark:bg-neutral-900/40 sm:h-32 sm:p-6 md:hover:-translate-y-2 md:hover:border-mont-violet/50 md:hover:bg-white/60 md:hover:shadow-xl dark:md:hover:bg-neutral-900/60";

          const stackedClasses =
            "absolute inset-0 flex flex-col items-center justify-center rounded-2xl border border-neutral-200/20 bg-white/85 p-6 shadow-[0_15px_35px_rgba(0,0,0,0.08)] transition-colors duration-500 glass-panel dark:bg-neutral-950/85";

          return (
            <div
              key={item.name}
              className={`brand-card group overflow-hidden ${isGrid ? gridClasses : stackedClasses}`}
              style={
                !isGrid
                  ? {
                      zIndex: SITE_TOOL_ENTRIES.length - relativeIdx,
                      transform: `translate3d(0px, ${relativeIdx * -12}px, ${-relativeIdx * 25}px) rotate(${relativeIdx * -2.5}deg)`,
                      opacity: 1 - relativeIdx * 0.12,
                      transition:
                        "transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.8s ease",
                      pointerEvents: isTop ? "auto" : "none",
                    }
                  : {}
              }
            >
              <div className="relative flex h-12 w-full items-center justify-center transition-transform duration-500 sm:h-16 md:group-hover:scale-108">
                {item.iconUrl ? (
                  <img
                    src={item.iconUrl}
                    alt={item.name}
                    className={`max-h-full max-w-[80%] object-contain drop-shadow-sm transition-all duration-500 
                      ${isGrid ? "opacity-90 md:group-hover:opacity-100" : isTop ? "opacity-100" : "opacity-50"}
                      ${item.iconUrl.includes("SAS") ? "dark:invert" : "dark:brightness-110"}
                    `}
                    loading="lazy"
                  />
                ) : (
                  <Layers className="h-8 w-8 text-mont-violet" />
                )}
              </div>

              <span
                className={`mt-2 text-center text-[10px] font-bold uppercase tracking-wider transition-all duration-500 sm:mt-3 sm:text-xs
                  ${isGrid ? "text-foreground opacity-80 md:group-hover:text-mont-violet md:group-hover:opacity-100" : isTop ? "font-extrabold text-mont-violet opacity-100" : "text-muted-foreground opacity-40"}
                `}
              >
                {item.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* Interaction cue — desktop hover vs touch tap */}
      {canHover && !isTabletOrBelow ? (
        <div
          className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-bold uppercase tracking-[0.3em] text-mont-violet transition-all duration-500 ${
            isGrid ? "pointer-events-none scale-90 opacity-0" : "scale-100 animate-pulse opacity-100"
          }`}
        >
          Hover to fan out
        </div>
      ) : (
        <div
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-[0.2em] text-mont-violet transition-all duration-500 sm:text-xs sm:tracking-[0.3em] ${
            isGrid ? "opacity-60" : "animate-pulse opacity-100"
          }`}
        >
          {isGrid ? "Tap to stack" : "Tap to fan out"}
        </div>
      )}
    </div>
  );
}
